"use client"

import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "@/lib/icons"
import { useTranslations } from "next-intl"
import type React from "react"

// Extracted from components/booru-detail-modal.tsx (pure move: same JSX, same behaviour).
interface ZoomControlsProps {
  canNavigate: { prev: boolean; next: boolean; } | undefined
  fullscreen: boolean
  handleBottomSwipeTouchEnd: (e: React.TouchEvent) => void
  handleBottomSwipeTouchStart: (e: React.TouchEvent) => void
  handleZoomIn: () => void
  handleZoomOut: () => void
  hideUI: boolean
  isAlbum: boolean
  isCurrentAlbumPageVideo: boolean
  isMediaVideo: boolean
  onNavigate: ((direction: "prev" | "next") => void) | undefined
  setZoom: React.Dispatch<React.SetStateAction<number>>
  zoom: number
}

export function ZoomControls({
  canNavigate,
  fullscreen,
  handleBottomSwipeTouchEnd,
  handleBottomSwipeTouchStart,
  handleZoomIn,
  handleZoomOut,
  hideUI,
  isAlbum,
  isCurrentAlbumPageVideo,
  isMediaVideo,
  onNavigate,
  setZoom,
  zoom,
}: ZoomControlsProps) {
  const tt = useTranslations("booruDetailModal")
  return (
    <>
      {!hideUI && !fullscreen && !isMediaVideo && !isCurrentAlbumPageVideo && !isAlbum && (
                <div
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 z-20"
                  onClick={(e) => e.stopPropagation()}
                  onTouchStart={handleBottomSwipeTouchStart}
                  onTouchEnd={handleBottomSwipeTouchEnd}
                >
                  {/* Drag handle — swipe up to close */}
                  <div className="w-8 h-1 rounded-full bg-white/30" />
                <div className="flex items-center gap-1.5 bg-black/60 rounded-full px-2 py-1.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-[30px] w-[30px] text-white/80 hover:text-white hover:bg-white/10 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-90"
                    onClick={() => onNavigate?.("prev")}
                    disabled={!onNavigate || !canNavigate?.prev}
                    title={tt("previous")}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-[30px] w-[30px] text-white/80 hover:text-white hover:bg-white/10 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-90"
                    onClick={handleZoomOut}
                    disabled={zoom <= 0.1}
                    title={tt("zoomOut")}
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>

                  <button
                    className="px-2 text-xs text-white/80 hover:text-white transition-all duration-150 min-w-[3rem] text-center active:scale-95"
                    onClick={() => setZoom(1)}
                    title={tt("resetZoom")}
                  >
                    {Math.round(zoom * 100)}%
                  </button>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-[30px] w-[30px] text-white/80 hover:text-white hover:bg-white/10 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-90"
                    onClick={handleZoomIn}
                    disabled={zoom >= 5}
                    title={tt("zoomIn")}
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-[30px] w-[30px] text-white/80 hover:text-white hover:bg-white/10 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-150 active:scale-90"
                    onClick={() => onNavigate?.("next")}
                    disabled={!onNavigate || !canNavigate?.next}
                    title={tt("next")}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                </div>
              )}
    </>
  )
}
